import i18n from '@/i18n'
import { supportedLocales } from '@/config/locales'

type Translate = (key: string | string[], options?: any) => string
type ParameterTranslator = (value: string, t: Translate) => string
const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Registered source templates preserve dates, amounts and identifiers as parameters.
// A new message needs one stable key and a translation in each language catalogue.
let patternCache = new WeakMap<object, ReturnType<typeof compilePatterns>>()
i18n.on('languageChanged', () => { patternCache = new WeakMap() })
function compilePatterns(messages: Record<string, string>) {
 return Object.keys(messages).map(key => {
  const text = messages[key]
  if (typeof text !== 'string' || !text.includes('{{')) return undefined
  const parameters: string[] = []
  let offset = 0
  let pattern = '^'
  for (const match of text.matchAll(/{{(\w+)}}/g)) {
    pattern += escape(text.slice(offset, match.index)) + '([\\s\\S]+?)'
    parameters.push(match[1])
    offset = match.index! + match[0].length
  }
  return { key, parameters, types: i18n.getResourceBundle('en', 'messageParameters')?.[key] ?? {}, pattern: new RegExp(pattern + escape(text.slice(offset)) + '$') }
 }).filter((entry): entry is NonNullable<typeof entry> => !!entry)
}

export function localizeCatalogMessage(message: string, t: Translate, translateParameter: ParameterTranslator, depth = 0): string | undefined {
  const sources = supportedLocales.map(({code}) => i18n.getResourceBundle(code, 'runtimeMessages')).filter(Boolean)
  for (const source of sources) {
   let patterns = patternCache.get(source)
   if (!patterns) { patterns = compilePatterns(source); patternCache.set(source, patterns) }
   for (const { key, parameters, types, pattern } of patterns) {
    const match = pattern.exec(message)
    if (!match) continue
    const values = Object.fromEntries(parameters.map((name, index) => {
      const value = match[index + 1]
      if (['attribute', 'subject'].includes(name)) return [name, translateParameter(value, t)]
      if (types[name] === 'enum') return [name, t(value)]
      if (types[name] === 'message' && depth < 2) return [name, t(value) !== value ? t(value) : localizeCatalogMessage(value, t, translateParameter, depth + 1) ?? value]
      return [name, value]
    }))
    const translated = t(key, values)
    if (translated !== key) return translated
  }
  }
  return undefined
}
